// TenantAiQuotaModal — super_admin 管理單一 tenant 的 AI token 額度。
//
// 兩個獨立池：
//   1. 月度 cap（resets monthly）— PUT /v1/platform/tenants/:id/ai-quota
//      改 tenant_feature_overrides.ai_chat.tokens_per_month_cap
//   2. 加值券 grants — POST/DELETE /v1/platform/tenants/:id/ai-grants
//
// 文案 / source-of-truth：apps/ai-api/src/token-quota.ts header。

import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Sparkles, Coins, Save, Plus, XCircle, AlertTriangle, Trash2 } from "lucide-react";
import Drawer from "../../components/Drawer.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";
import {
  getTenantAiQuota,
  putTenantAiQuota,
  issueAiGrant,
  revokeAiGrant,
  friendlyPlatformError,
  type AiQuotaSnapshot,
  type AiGrantRow,
} from "../../lib/platformApi.js";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: AiQuotaSnapshot }
  | { status: "error"; message: string };

function fmt(n: number): string {
  if (n === -1) return "無限制";
  return n.toLocaleString("en-US");
}

function fmtTokens(n: number): string {
  if (n === -1) return "無限制";
  if (n === 0) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return n.toLocaleString("en-US");
}

function relativeDate(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

export default function TenantAiQuotaModal({
  open,
  tenantId,
  tenantName,
  onClose,
}: {
  open: boolean;
  tenantId: string | null;
  tenantName: string;
  onClose: () => void;
}): React.ReactElement | null {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const load = useCallback(async () => {
    if (!tenantId) return;
    setState({ status: "loading" });
    try {
      const data = await getTenantAiQuota(tenantId);
      setState({ status: "ready", data });
    } catch (e) {
      setState({ status: "error", message: friendlyPlatformError(e) });
    }
  }, [tenantId]);

  useEffect(() => {
    if (open && tenantId) void load();
  }, [open, tenantId, load]);

  if (!open || !tenantId) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`AI 額度管理：${tenantName}`}
      description="調整每月配額、加值一次性 token、查看用量與加值券歷史"
      size="lg"
    >
      {state.status === "loading" && (
        <div className="text-sm text-text-secondary">載入中…</div>
      )}
      {state.status === "error" && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {state.message}
          <button className="ml-2 underline" onClick={() => void load()}>再試一次</button>
        </div>
      )}
      {state.status === "ready" && (
        <Body data={state.data} tenantId={tenantId} onReload={() => void load()} />
      )}
    </Drawer>
  );
}

function Body({
  data,
  tenantId,
  onReload,
}: {
  data: AiQuotaSnapshot;
  tenantId: string;
  onReload: () => void;
}): React.ReactElement {
  return (
    <div className="space-y-6">
      <Snapshot data={data} onReload={onReload} />
      <MonthlyCapEditor data={data} tenantId={tenantId} onSaved={onReload} />
      <GrantsList data={data} tenantId={tenantId} onChanged={onReload} />
      <IssueGrantForm tenantId={tenantId} onIssued={onReload} />
    </div>
  );
}

function Snapshot({ data, onReload }: { data: AiQuotaSnapshot; onReload: () => void }): React.ReactElement {
  const { resolved, usage_this_month, grants, total_available, plan, override } = data;
  const monthlyRemaining = resolved.cap === -1 ? -1 : Math.max(0, resolved.cap - usage_this_month);
  const monthlyPercent = resolved.cap > 0
    ? Math.min(100, Math.round((usage_this_month / resolved.cap) * 100))
    : (resolved.cap === -1 ? 0 : 100);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" /> 目前額度狀態
        </h3>
        <button className="btn-ghost text-xs" onClick={onReload} title="重新整理">
          <RefreshCw className="h-3 w-3" /> 重新整理
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-border bg-bg-base/40 p-3">
          <div className="text-2xs text-text-muted">本月已用</div>
          <div className="mt-1 text-lg font-semibold text-text-primary">{fmtTokens(usage_this_month)}</div>
          <div className="mt-0.5 text-2xs text-text-muted">/ {fmtTokens(resolved.cap)} 上限</div>
          {resolved.cap > 0 && (
            <div className="mt-2 h-1.5 rounded bg-border overflow-hidden">
              <div
                className={`h-full ${monthlyPercent >= 95 ? "bg-danger" : monthlyPercent >= 80 ? "bg-warning" : "bg-accent"}`}
                style={{ width: `${monthlyPercent}%` }}
              />
            </div>
          )}
        </div>
        <div className="rounded-lg border border-border bg-bg-base/40 p-3">
          <div className="text-2xs text-text-muted">本月可用餘額</div>
          <div className="mt-1 text-lg font-semibold text-text-primary">{fmtTokens(monthlyRemaining)}</div>
          <div className="mt-0.5 text-2xs text-text-muted">下月 1 號 UTC 重置</div>
        </div>
        <div className="rounded-lg border border-border bg-bg-base/40 p-3">
          <div className="text-2xs text-text-muted flex items-center gap-1"><Coins className="h-3 w-3" /> 加值券剩餘</div>
          <div className="mt-1 text-lg font-semibold text-text-primary">{fmtTokens(grants.remaining)}</div>
          <div className="mt-0.5 text-2xs text-text-muted">{grants.active.length} 張有效</div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-bg-base/40 p-3 text-xs space-y-1.5">
        <div className="flex justify-between">
          <span className="text-text-muted">總可用 (月度餘額 + 加值券)</span>
          <span className="font-semibold text-text-primary">{fmtTokens(total_available)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">套餐</span>
          <span className="text-text-primary">{plan.display_name}（id: <code className="text-2xs">{plan.id}</code>）</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">套餐預設月度上限</span>
          <span className="text-text-primary">{plan.cap === null ? "—" : fmtTokens(plan.cap)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">套餐 AI 開關</span>
          <span className="text-text-primary">{plan.enabled === null ? "—" : plan.enabled ? "啟用" : "停用"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">目前生效</span>
          <span className="text-text-primary">
            {resolved.enabled ? "AI 啟用" : "AI 停用"}（{enabledSourceLabel(resolved.enabled_source)}）·
            上限 {fmtTokens(resolved.cap)}（{capSourceLabel(resolved.cap_source)}）
          </span>
        </div>
        {override.value && (
          <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
            <span className="text-text-muted">調整備註</span>
            <span className="text-text-primary truncate ml-2 max-w-xs" title={override.reason ?? undefined}>
              {override.reason || "（無備註）"} · 更新於 {override.updated_at?.slice(0, 16).replace("T", " ")}
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function capSourceLabel(s: "override" | "plan" | "default"): string {
  if (s === "override") return "來自個別調整";
  if (s === "plan") return "來自套餐預設";
  return "系統預設 500k";
}
function enabledSourceLabel(s: "override" | "plan" | "default"): string {
  if (s === "override") return "個別調整";
  if (s === "plan") return "套餐預設";
  return "系統預設";
}

function MonthlyCapEditor({
  data,
  tenantId,
  onSaved,
}: {
  data: AiQuotaSnapshot;
  tenantId: string;
  onSaved: () => void;
}): React.ReactElement {
  const override = data.override.value;
  // Local form state — display empty string when no cap override (so the
  // input shows the plan default as placeholder rather than the literal
  // number).
  const [capInput, setCapInput] = useState<string>(
    typeof override?.tokens_per_month_cap === "number" ? String(override.tokens_per_month_cap) : "",
  );
  const [enabledOverride, setEnabledOverride] = useState<"" | "true" | "false">(
    typeof override?.enabled === "boolean" ? (override.enabled ? "true" : "false") : "",
  );
  const [reason, setReason] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (mode: "set" | "clear") => {
    setBusy(true); setErr(null);
    try {
      if (mode === "clear") {
        await putTenantAiQuota(tenantId, {
          tokens_per_month_cap: null,
          enabled: null,
          reason: reason || null,
        });
      } else {
        const capNum = capInput === "" ? null : Number(capInput);
        if (capNum !== null && (!Number.isInteger(capNum) || (capNum !== -1 && capNum < 0))) {
          throw new Error("月度上限必須是 ≥ 0 的整數，或 -1 代表無限制");
        }
        const enabledVal = enabledOverride === "" ? null : enabledOverride === "true";
        await putTenantAiQuota(tenantId, {
          tokens_per_month_cap: capNum,
          enabled: enabledVal,
          reason: reason || null,
        });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : friendlyPlatformError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="space-y-3 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-text-primary">月度上限調整</h3>
      <p className="text-2xs text-text-muted leading-relaxed">
        在此調整僅針對此單位生效，每月月底重置後仍持續套用。
        留空後點「清除調整」可移除設定，恢復套餐預設值。
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1.5">
          <span className="text-2xs text-text-secondary">月度 token 上限</span>
          <input
            className="input"
            placeholder={data.plan.cap === null ? "（套餐未設定，系統預設 500k）" : `預設 ${fmt(data.plan.cap)}`}
            value={capInput}
            onChange={(e) => setCapInput(e.target.value.trim())}
            disabled={busy}
          />
          <span className="text-2xs text-text-muted">整數；-1 = 無限制；留空 = 不調整</span>
        </label>
        <label className="space-y-1.5">
          <span className="text-2xs text-text-secondary">AI 啟用調整</span>
          <select
            className="input"
            value={enabledOverride}
            onChange={(e) => setEnabledOverride(e.target.value as "" | "true" | "false")}
            disabled={busy}
          >
            <option value="">（跟隨套餐）</option>
            <option value="true">強制啟用</option>
            <option value="false">強制停用</option>
          </select>
          <span className="text-2xs text-text-muted">套餐已是想要的設定就維持「跟隨套餐」</span>
        </label>
      </div>
      <label className="block space-y-1.5">
        <span className="text-2xs text-text-secondary">備註（必填，方便日後在操作紀錄中追溯）</span>
        <input
          className="input"
          placeholder="例：客戶簽 6 個月特殊合約、暫時升級到 1M / 月"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          maxLength={200}
        />
      </label>
      {err && <div className="text-xs text-danger">{err}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary text-sm" disabled={busy} onClick={() => void save("set")}>
          <Save className="h-3.5 w-3.5" /> 套用調整
        </button>
        <button
          className="btn-secondary text-sm"
          disabled={busy || !data.override.value}
          onClick={() => void save("clear")}
        >
          <XCircle className="h-3.5 w-3.5" /> 清除調整（恢復套餐預設）
        </button>
      </div>
    </section>
  );
}

function IssueGrantForm({
  tenantId,
  onIssued,
}: {
  tenantId: string;
  onIssued: () => void;
}): React.ReactElement {
  const [amountInput, setAmountInput] = useState("100000");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); // YYYY-MM-DD or empty
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const issue = async () => {
    setBusy(true); setErr(null);
    try {
      const amt = Number(amountInput);
      if (!Number.isInteger(amt) || amt <= 0 || amt > 100_000_000) {
        throw new Error("加值數量必須是 1 ~ 100,000,000 之間的整數");
      }
      let expiresIso: string | null = null;
      if (expiresAt) {
        const d = new Date(`${expiresAt}T23:59:59Z`);
        if (Number.isNaN(d.getTime())) throw new Error("到期日格式錯誤");
        if (d.getTime() <= Date.now()) throw new Error("到期日必須晚於現在");
        expiresIso = d.toISOString();
      }
      await issueAiGrant(tenantId, {
        amount: amt,
        reason: reason || null,
        expires_at: expiresIso,
      });
      // Reset form for next grant
      setAmountInput("100000");
      setReason("");
      setExpiresAt("");
      onIssued();
    } catch (e) {
      setErr(e instanceof Error ? e.message : friendlyPlatformError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="space-y-3 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
        <Coins className="h-4 w-4 text-accent" /> 加值（一次性 token grant）
      </h3>
      <p className="text-2xs text-text-muted leading-relaxed">
        加值不會月底重置；月度 cap 用完才會開始扣 grants。撤銷時 grant 立即從可用池消失。
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="space-y-1.5">
          <span className="text-2xs text-text-secondary">加值 token 數量</span>
          <input
            className="input"
            placeholder="100000"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value.replace(/[^\d]/g, ""))}
            disabled={busy}
            inputMode="numeric"
          />
          <span className="text-2xs text-text-muted">建議格式：100k = 100000、1M = 1000000</span>
        </label>
        <label className="space-y-1.5">
          <span className="text-2xs text-text-secondary">到期日（可選）</span>
          <input
            type="date"
            className="input"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            disabled={busy}
            min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
          />
          <span className="text-2xs text-text-muted">留空 = 永不過期</span>
        </label>
      </div>
      <label className="block space-y-1.5">
        <span className="text-2xs text-text-secondary">加值原因（必填）</span>
        <input
          className="input"
          placeholder="例：服務中斷補償 100k token"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          maxLength={200}
        />
      </label>
      {err && <div className="text-xs text-danger">{err}</div>}
      <button
        className="btn-primary text-sm"
        disabled={busy || !amountInput || !reason}
        onClick={() => void issue()}
      >
        <Plus className="h-3.5 w-3.5" /> 加值
      </button>
    </section>
  );
}

function GrantsList({
  data,
  tenantId,
  onChanged,
}: {
  data: AiQuotaSnapshot;
  tenantId: string;
  onChanged: () => void;
}): React.ReactElement {
  const dlg = useConfirmDialog();
  const { active, history } = data.grants;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const revoke = async (g: AiGrantRow) => {
    const ok = await dlg.confirm(
      `加值金額 ${fmt(g.amount)}（已用 ${fmt(g.consumed_amount)}）將從可用池立即消失，撤銷後無法復原。`,
      { title: "撤銷此筆加值？", okLabel: "確定撤銷", destructive: true },
    );
    if (!ok) return;
    setBusyId(g.id); setErr(null);
    try {
      await revokeAiGrant(tenantId, g.id, "super_admin 撤銷");
      onChanged();
    } catch (e) {
      setErr(friendlyPlatformError(e));
    } finally { setBusyId(null); }
  };

  return (
    <section className="space-y-3 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-text-primary">加值券</h3>
      {err && <div className="text-xs text-danger">{err}</div>}
      {active.length === 0 && history.length === 0 && (
        <div className="text-2xs text-text-muted">目前沒有任何加值紀錄。</div>
      )}
      {active.length > 0 && (
        <div className="space-y-2">
          <div className="text-2xs text-text-muted">目前有效（{active.length} 筆）</div>
          {active.map((g) => (
            <GrantRow key={g.id} grant={g} busy={busyId === g.id} onRevoke={() => void revoke(g)} />
          ))}
        </div>
      )}
      {history.filter((g) => g.revoked_at || isExpired(g) || g.consumed_amount >= g.amount).length > 0 && (
        <details className="space-y-2">
          <summary className="text-2xs text-text-muted cursor-pointer hover:text-text-secondary">
            歷史紀錄（已撤銷 / 已用完 / 已過期）
          </summary>
          <div className="mt-2 space-y-2">
            {history
              .filter((g) => g.revoked_at || isExpired(g) || g.consumed_amount >= g.amount)
              .map((g) => (
                <GrantRow key={g.id} grant={g} busy={false} onRevoke={null} dim />
              ))}
          </div>
        </details>
      )}
    </section>
  );
}

function isExpired(g: AiGrantRow): boolean {
  if (!g.expires_at) return false;
  return new Date(g.expires_at).getTime() <= Date.now();
}

function GrantRow({
  grant: g,
  busy,
  onRevoke,
  dim = false,
}: {
  grant: AiGrantRow;
  busy: boolean;
  onRevoke: (() => void) | null;
  dim?: boolean;
}): React.ReactElement {
  const remaining = Math.max(0, g.amount - g.consumed_amount);
  const consumed = g.consumed_amount;
  const expired = isExpired(g);
  const status = g.revoked_at ? "已撤銷" : expired ? "已過期" : remaining === 0 ? "已用完" : "有效";
  return (
    <div className={`rounded-lg border border-border p-3 ${dim ? "opacity-60" : "bg-bg-base/40"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-text-primary">
          {fmt(g.amount)} token{" "}
          <span className="text-2xs text-text-muted">餘 {fmt(remaining)} · 已用 {fmt(consumed)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-2xs ${status === "有效" ? "text-accent" : "text-text-muted"}`}>{status}</span>
          {onRevoke && (
            <button
              className="btn-ghost h-7 w-7 p-0 text-danger"
              title="撤銷"
              disabled={busy}
              onClick={onRevoke}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-1.5 text-2xs text-text-muted leading-relaxed">
        加值時間 {relativeDate(g.granted_at)} · 到期 {relativeDate(g.expires_at)}
        {g.reason && (<> · 原因「{g.reason}」</>)}
        {g.revoked_reason && (<> · 撤銷原因「{g.revoked_reason}」</>)}
      </div>
    </div>
  );
}
